import { supabase } from '../config/supabase';
import { defaultPairingCompatibilityConfig, type PairingCalculationConfig } from './pairingCompatibilityAnalytics';

const storageKey = 'courtcaptain:pairing-compatibility-config';
const settingKey = 'pairing_compatibility_config';

export type PairingCompatibilityConfigDraft = PairingCalculationConfig;

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, Number(parsed.toFixed(2))));
}

export function normalizePairingCompatibilityConfig(value: unknown): PairingCalculationConfig {
  const source = value && typeof value === 'object' ? value as Partial<PairingCalculationConfig> : {};
  const sourceWeights = (source.weights || {}) as Partial<PairingCalculationConfig['weights']>;
  const defaultWeights = defaultPairingCompatibilityConfig.weights;
  const sourceThresholds = (source.thresholds || {}) as Partial<PairingCalculationConfig['thresholds']>;
  const defaultThresholds = defaultPairingCompatibilityConfig.thresholds;
  const sourceEvidence = (source.evidenceThresholds || {}) as Partial<PairingCalculationConfig['evidenceThresholds']>;
  const defaultEvidence = defaultPairingCompatibilityConfig.evidenceThresholds;
  const neutral = clampNumber(sourceThresholds.neutral, defaultThresholds.neutral, 0, 100);
  const good = clampNumber(sourceThresholds.good, defaultThresholds.good, neutral, 100);
  const excellent = clampNumber(sourceThresholds.excellent, defaultThresholds.excellent, good, 100);

  return {
    algorithmVersion: typeof source.algorithmVersion === 'string' && source.algorithmVersion.trim()
      ? source.algorithmVersion.trim()
      : defaultPairingCompatibilityConfig.algorithmVersion,
    weights: {
      results: clampNumber(sourceWeights.results, defaultWeights.results, 0, 100),
      dominance: clampNumber(sourceWeights.dominance, defaultWeights.dominance, 0, 100),
      adaptability: clampNumber(sourceWeights.adaptability, defaultWeights.adaptability, 0, 100),
      pressurePerformance: clampNumber(sourceWeights.pressurePerformance, defaultWeights.pressurePerformance, 0, 100),
      closingPerformance: clampNumber(sourceWeights.closingPerformance, defaultWeights.closingPerformance, 0, 100),
      consistency: clampNumber(sourceWeights.consistency, defaultWeights.consistency, 0, 100),
      synergy: clampNumber(sourceWeights.synergy, defaultWeights.synergy, 0, 100),
      lineContext: clampNumber(sourceWeights.lineContext, defaultWeights.lineContext, 0, 100),
    },
    thresholds: { neutral, good, excellent },
    evidenceThresholds: {
      minimumMatches: Math.round(clampNumber(sourceEvidence.minimumMatches, defaultEvidence.minimumMatches, 1, 20)),
      minimumRegulationSets: Math.round(clampNumber(sourceEvidence.minimumRegulationSets, defaultEvidence.minimumRegulationSets, 0, 60)),
      minimumMatchesWithScores: Math.round(clampNumber(sourceEvidence.minimumMatchesWithScores, defaultEvidence.minimumMatchesWithScores, 0, 20)),
    },
    currentSeasonWeight: clampNumber(source.currentSeasonWeight, defaultPairingCompatibilityConfig.currentSeasonWeight, 0, 3),
    priorSeasonWeight: clampNumber(source.priorSeasonWeight, defaultPairingCompatibilityConfig.priorSeasonWeight, 0, 3),
    olderSeasonWeight: clampNumber(source.olderSeasonWeight, defaultPairingCompatibilityConfig.olderSeasonWeight, 0, 3),
  };
}

export function pairingCompatibilityWeightTotal(config: Pick<PairingCalculationConfig, 'weights'>): number {
  return Object.values(config.weights).reduce((sum, weight) => sum + Number(weight || 0), 0);
}

function parseStoredConfig(value: string | null | undefined): PairingCalculationConfig {
  if (!value) return defaultPairingCompatibilityConfig;
  try {
    return normalizePairingCompatibilityConfig(JSON.parse(value));
  } catch {
    return defaultPairingCompatibilityConfig;
  }
}

function loadLocalConfig(): PairingCalculationConfig {
  try {
    return parseStoredConfig(window.localStorage.getItem(storageKey));
  } catch {
    return defaultPairingCompatibilityConfig;
  }
}

function saveLocalConfig(config: PairingCalculationConfig): void {
  window.localStorage.setItem(storageKey, JSON.stringify(config));
}

export async function loadPairingCompatibilityConfig(): Promise<PairingCalculationConfig> {
  const localConfig = loadLocalConfig();
  const { data, error } = await supabase
    .from('app_settings')
    .select('setting_value')
    .eq('setting_key', settingKey)
    .maybeSingle();
  if (error) return localConfig;
  const config = parseStoredConfig((data as { setting_value?: string } | null)?.setting_value);
  saveLocalConfig(config);
  return config;
}

export async function savePairingCompatibilityConfig(config: PairingCalculationConfig): Promise<PairingCalculationConfig> {
  const normalized = normalizePairingCompatibilityConfig(config);
  saveLocalConfig(normalized);
  const { error } = await supabase
    .from('app_settings')
    .upsert({ setting_key: settingKey, setting_value: JSON.stringify(normalized) }, { onConflict: 'setting_key' });
  if (error) throw error;
  return normalized;
}
